import { useEffect } from 'react';

import { apiClient } from '@/shared/lib/axios';
import {
  createActiveSessionSyncTrigger,
  resolveActiveSessionSyncConfig,
  runWithActiveSessionSyncTransition,
} from './activeSessionSync';
import { runP1SsoAuthMutation } from './ssoAuthMutationLock';
import { hideSsoSyncCurtain, isUserPageReload } from './ssoSyncCurtain';
import type { SsoContext } from './ssoSession';

// The session sync runs exactly once per page load. It used to also run on `focus` and
// `visibilitychange`, which turned every tab switch into a broker round trip and therefore
// a full page reload. The flag lives at module scope on purpose: a successful sync issues a
// new session, and the resulting re-render/route change re-ran the effect, which synced
// again, which looped until the rate limiter stopped it. Reloading the page, opening a new
// tab or typing the URL is a new boot, so cross-site session sharing still happens there.
let bootSyncStarted = false;

export const useActiveSessionSync = (
  context: SsoContext | undefined,
  authenticated: boolean,
): void => {
  const site = context?.site;
  const entryType = context?.entryType;
  const config = resolveActiveSessionSyncConfig(
    import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED,
    import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_COOLDOWN_MS,
  );

  useEffect(() => {
    if (
      !authenticated ||
      bootSyncStarted ||
      site === undefined ||
      entryType === undefined ||
      config === undefined
    ) {
      return;
    }
    bootSyncStarted = true;

    const activeContext: SsoContext = { site, entryType };
    // The curtain is raised in the entry module, before anything paints. It belongs to the
    // navigation it was raised for, so anything short of that navigation must reveal the app.
    let navigated = false;
    const activeSyncTrigger = createActiveSessionSyncTrigger({
      context: activeContext,
      cooldownMs: config.cooldownMs,
      storage: window.sessionStorage,
      authGenerationStorage: window.localStorage,
      location: () => ({
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        // Carried so the redirect guard can tell a downgrade from a deployment that simply has
        // no certificate. Without it an http-only environment reached by IP:port never follows
        // the broker URL, and the sync fails silently.
        protocol: window.location.protocol,
      }),
      now: Date.now,
      request: (endpoint, payload) => runWithActiveSessionSyncTransition(async () => {
        const response = await apiClient.post(endpoint, payload, {
          withCredentials: true,
        });
        return response.data?.data;
      }),
      navigate: redirectUrl => {
        navigated = true;
        window.location.replace(redirectUrl);
      },
      userReload: isUserPageReload(),
    });

    void runP1SsoAuthMutation({
      context: activeContext,
      ssoEnabled: true,
      storage: window.localStorage,
      operation: activeSyncTrigger,
    }).finally(() => {
      if (!navigated) {
        hideSsoSyncCurtain();
      }
    });
  }, [authenticated, config?.cooldownMs, entryType, site]);
};
